import { matchQuery } from './matchQuery';
import { pathToRegexp } from './pathToRegexp';
import { type Tree, Node } from './treeifyRoutes';
import type { Query, InvariantRoute } from './types';

/**
 * Helper function to prioritize nodes with capturing groups in their segment patterns.
 * Nodes with capturing groups (e.g., `:id(\\d+)`) are considered more specific than
 * nodes without them (e.g., `:id`), so they are placed at the beginning of the array.
 *
 * @param arr - Array of nodes to modify
 * @param node - Node to add to the array
 *
 * @example
 * // Node with pattern `:id(\\d+)` will be placed before node with pattern `:id`
 * pushOrUnshiftByCaptureGroup(nodes, nodeWithCaptureGroup);
 */
function pushOrUnshiftByCaptureGroup<T extends InvariantRoute>(arr: Node<T>[], node: Node<T>) {
	if (node.segmentPattern.includes('(') && node.segmentPattern.includes(')')) {
		// Segment pattern has capturing group, making it more specific
		// Place it at the beginning for higher priority
		arr.unshift(node);
	} else {
		// No capturing group, place at the end of the array
		arr.push(node);
	}
}

// Find matching nodes by segment
// sort the nodes by specificity
function matchChildren<T extends InvariantRoute>(node: Node<T>, segments: string[]) {
	// how do we define specificity? This is a tricky question.
	// the specificity goes like this:
	// 1. if the segment is an exact match, of course it's the most specific
	// 2. the rest is regex match, within the regex match, we have to consider:
	// 		2.1 the length of segments and if node has descendants. /jira/:id/summary is more specific than /jira/:id if the request URL is /jira/123/summary
	// 3 after checking the length, we have to consider if the segmentPattern has any capturing group. /jira/:id(\d+) is more specific than /jira/:id
	//
	// This is not a comprehensive solution to the specificity problem
	// I will use production urls to verify this heuristic

	const exactMatch: Node<T>[] = []; // segment is an exact match e.g. /jira matches /jira
	const lengthMatch: Node<T>[] = []; // check #2.1 from the above comment
	const rest: Node<T>[] = [];

	const { children } = node;
	// treat url segment as empty string if it's undefined
	// possible if we have optional segmentPattern
	const segment = segments[node.level] || '';
	// check if there is next segment
	const hasNextSegment = segments.length > node.level;

	for (const segmentPattern in children) {
		if (Object.prototype.hasOwnProperty.call(children, segmentPattern)) {
			const child = children[segmentPattern];

			if (segment === segmentPattern) {
				// we have exact segment match
				exactMatch.push(child);
			} else {
				const regex = pathToRegexp(segmentPattern, [], {
					end: true,
					strict: true,
					sensitive: false,
				});
				if (regex.test(segment)) {
					const nodeAhasChildren = Object.keys(child.children).length > 0;

					if (hasNextSegment && nodeAhasChildren) {
						// if there is a next segment, we should prioritize nodes with children
						pushOrUnshiftByCaptureGroup(lengthMatch, child);
					} else if (!hasNextSegment && !nodeAhasChildren) {
						// if there is no next segment, we should prioritize nodes without children
						pushOrUnshiftByCaptureGroup(lengthMatch, child);
					} else {
						pushOrUnshiftByCaptureGroup(rest, child);
					}
				}
			}
		}
	}

	return [...exactMatch, ...lengthMatch, ...rest];
}

/**
 * Recursively searches for routes in optional segment nodes.
 * This function handles patterns like `/:a?/:b?/:c?` where segments can be omitted.
 * It traverses the tree looking for routes that can match with missing segments.
 *
 * @param node - Current node to examine
 * @param queryParams - Query parameters to match against route constraints
 * @returns Matched route if found, undefined otherwise
 *
 * @example
 * // For pattern `/:projectId?/:viewType?` and URL `/`
 * // This function will check if the `:projectId?` node has a matching route
 */
function recursivelyFindOptionalNodes<T extends InvariantRoute>(
	node: Node<T>,
	queryParams: Query = {},
) {
	const { segmentPattern, children, routes } = node;

	// Only process nodes with optional segment patterns (ending with '?')
	if (segmentPattern.endsWith('?')) {
		// Try to find a route at this level that matches the query parameters
		const maybeMatchedRoute = matchRoutesByQuery(routes, queryParams);
		if (maybeMatchedRoute) {
			return maybeMatchedRoute;
		}

		// If no route found at this level, recursively check child nodes
		for (const key in children) {
			if (Object.prototype.hasOwnProperty.call(children, key)) {
				return recursivelyFindOptionalNodes(children[key], queryParams);
			}
		}
	}
}

/**
 * Matches routes based on query parameters, returning the most specific match.
 * This function filters routes first (for efficiency) then sorts by specificity.
 *
 * Specificity ranking (highest to lowest):
 * 1. Routes with more query parameters (length-based)
 * 2. Routes with exact value matches (e.g., "param=value")
 * 3. Routes ordered by __unsafeOrder (fallback for ties)
 */
function matchRoutesByQuery<T extends InvariantRoute>(
	routes: T[],
	queryParamObject: Query,
): T | null {
	// Early return if no routes to process
	if (routes.length === 0) {
		return null;
	}

	// STEP 1: Filter routes to only include those that match the query parameters
	// This is done first for performance - no point sorting routes that won't match
	const filteredRoutes = routes.filter((route) => {
		// Routes without query constraints match any query parameters
		if (route.query === undefined) {
			return true;
		}

		// Create a fake match object for the matchQuery function
		// We only need to check if the query matches, not extract actual values
		const fakeMatch = { params: {}, query: {}, isExact: false, path: '', url: '' };

		// Use the matchQuery function to determine if this route's query constraints
		// are satisfied by the current query parameters
		return !!matchQuery(route.query, queryParamObject, fakeMatch);
	});

	// Early return if no routes match the query parameters
	if (filteredRoutes.length === 0) {
		return null;
	}

	// STEP 2: Sort the filtered routes by specificity (most specific first)
	const sortedRoutes = filteredRoutes.sort((a, b) => {
		// 1st sort criterion: number of query parameters
		// Routes with more query parameters are considered more specific
		const aQueryLength = a.query?.length || 0;
		const bQueryLength = b.query?.length || 0;

		// If query lengths differ, prioritize the route with more parameters
		if (bQueryLength !== aQueryLength) {
			return bQueryLength - aQueryLength; // Descending order (more params first)
		}

		// 2nd sort criterion: query parameter specificity type
		// Helper function to classify the specificity of query parameters
		const getQuerySpecificity = (query: string[] | undefined) => {
			if (!query) {
				return 0;
			}

			// These are most specific as they require exact parameter values (e.g., "mode=edit", "type=issue")
			if (query.some((q) => q.includes('='))) {
				return 1; // Medium specificity
			}

			return 0;
		};

		const aSpecificity = getQuerySpecificity(a.query);
		const bSpecificity = getQuerySpecificity(b.query);

		// If specificity levels differ, prioritize higher specificity
		if (aSpecificity !== bSpecificity) {
			return bSpecificity - aSpecificity; // Descending order (higher specificity first)
		}

		// 3rd sort criterion: fallback to __unsafeOrder for deterministic sorting
		const aOrder = a.__unsafeOrder || 0;
		const bOrder = b.__unsafeOrder || 0;
		return aOrder - bOrder; // Ascending order (lower order first)
	});

	// Return the most specific route that matches the query parameters
	// Since the array is sorted by specificity (most specific first),
	// the first element is our best match
	return sortedRoutes[0];
}

/**
 * Main route finding function that traverses a route tree to find the best matching route.
 *
 * This function implements a breadth-first search algorithm with specificity
 * handling to find the most appropriate route for a given URL path and query parameters.
 *
 * Algorithm Overview:
 * 1. Parse the URL path into segments
 * 2. Use a queue-based traversal to explore route tree nodes
 * 3. At each node, evaluate different matching scenarios:
 *    - Leaf nodes (no children): match query and check exact constraints
 *    - End of segments: look for routes at current node
 *    - Fewer segments than node level: check for optional patterns
 *    - More segments available: check non-exact routes and continue traversal
 * 4. Use matchChildren to find and prioritize child nodes by specificity
 * 5. Return the first matching route found (highest priority due to traversal order)
 *
 * @param tree - Pre-built route tree containing all possible routes
 * @param p - The URL pathname to match against
 * @param queryParams - Query parameters from the URL
 * @param basePath - Base path to strip from the URL before matching
 * @returns The best matching route, or null if no match found
 */
export function findRoute<T extends InvariantRoute>(
	tree: Tree<T>,
	p: string,
	queryParams: Query | undefined = {},
	basePath: string,
): T | null {
	// Remove base path to get the relative pathname for matching
	const pathname = p.replace(basePath, '');

	// Split pathname into individual segments for tree traversal
	// Example: '/jira/projects/123' becomes ['', 'jira', 'projects', '123']
	const segments = pathname.split('/');

	// Clean up segments array by removing empty strings from leading/trailing slashes
	if (segments[0] === '') {
		segments.shift(); // Remove leading empty string from initial '/'
	}
	if (segments[segments.length - 1] === '') {
		segments.pop(); // Remove trailing empty string from final '/'
	}

	// Initialize breadth-first search queue with the root node
	// The queue can contain either Node objects (to continue traversal) or
	// InvariantRoute objects (representing potential matches to return)
	const stack: Array<Node<T> | T> = [tree.root];

	// Safety mechanism to prevent infinite loops in malformed trees
	let count = 0;
	const maxCount = 2000;

	// Main traversal loop: continue until queue is empty or match found
	while (stack.length > 0 && count < maxCount) {
		count += 1;

		// Process next item from queue (FIFO for breadth-first behavior)
		const node = stack.shift();

		// TypeScript safety check (should never be null in practice)
		if (!node) {
			return null;
		}

		// If we encounter a route object instead of a Node, we've found our match
		// This happens when a non-exact route was added to the queue for later evaluation
		if (!(node instanceof Node)) {
			return node; // Return the matched route
		}

		// Extract node properties for evaluation
		const { children, routes, level } = node;

		let maybeMatchedRoute: T | null | undefined = null;
		let shouldMatchChildren = true;

		// SCENARIO 1: Leaf node (no children) - check for terminal route match
		if (Object.keys(children).length === 0) {
			// Structural integrity check - leaf nodes must have routes
			if (!routes.length) {
				throw new Error(
					`Route tree structural error: Reached a leaf node with no routes. ` +
						`This indicates the route tree was not built correctly. ` +
						`Every leaf node should have at least one route associated with it.`,
				);
			}

			// Attempt to match the routes at this leaf node
			maybeMatchedRoute = matchRoutesByQuery(routes, queryParams);

			if (maybeMatchedRoute) {
				// Check if we have unmatched segments remaining
				if (segments.length > level) {
					// We have leftover segments but nowhere to go in the tree
					// Only proceed if the route allows non-exact matching
					if (maybeMatchedRoute.exact) {
						// Route requires exact matching, so this isn't a valid match
						maybeMatchedRoute = null;
					}
					// If exact=false, the route can handle additional segments
				}
			}
		}
		// SCENARIO 2: End of URL segments - check for route at current node
		else if (segments.length === level) {
			// We've consumed all URL segments, see if current node has a matching route
			if (routes.length) {
				maybeMatchedRoute = matchRoutesByQuery(routes, queryParams);
			}
		}
		// SCENARIO 3: Fewer segments than node level - check for optional patterns
		else if (segments.length < level) {
			// We've run out of segments but the tree goes deeper
			// This can happen with optional patterns like /:a?/:b?/:c?
			shouldMatchChildren = false; // Don't continue normal traversal

			// Look for optional segment patterns that can match with missing segments
			maybeMatchedRoute = recursivelyFindOptionalNodes(node, queryParams);
		}
		// SCENARIO 4: More segments available - handle non-exact routes and continue
		else {
			// We have more segments to match and this node has children to explore
			// Check if current node has a non-exact route that could serve as fallback
			// Non-exact routes can match even when there are additional segments
			const lowSpecifityRoute = matchRoutesByQuery(routes, queryParams);
			if (lowSpecifityRoute && !lowSpecifityRoute.exact) {
				// Add this route to the queue for later consideration
				// It will be returned if no more specific match is found
				stack.unshift(lowSpecifityRoute);
			}
		}

		// If we found a match, return it immediately
		if (maybeMatchedRoute) {
			return maybeMatchedRoute;
		}

		// Continue tree traversal if appropriate
		if (shouldMatchChildren) {
			// Find child nodes that match the current segment, sorted by specificity
			const matchingChildren = matchChildren(node, segments);

			// Add matching children to the front of the queue for priority processing
			// More specific matches will be processed first due to sorting in matchChildren
			stack.unshift(...matchingChildren);
		}

		// Continue to next iteration to process the next item in the queue
	}

	// No match found after exhausting all possibilities
	return null;
}
